import React from "react";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceDot,
} from "recharts";
import { Navigation, DeviceThermostat, Air, WaterDrop } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useTimezone } from "../contexts/TimezoneContext";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatNumber, toDisplaySpeed, toDisplayTemperature } from "../utils/units";
import type { WeatherSpread } from "../types/weatherGrid";

dayjs.extend(utc);
dayjs.extend(timezone);

type RawWeatherRecord = Record<
    string,
    {
        wind_speed: number;
        wind_direction: number;
        temperature: number;
        relative_humidity: number;
    }
>;

/** [min, max] across the project's weather lattice, in display units. */
type DisplayRange = [number, number];

type ProcessedWeatherPoint = {
    timestamp: string;
    time: string;
    temperature: number;
    windSpeed: number;
    windDir: number;
    humidity: number;
    temperatureUnit: string;
    speedUnit: string;
    timezone: string;
    /** Present only when a spread was supplied for this hour. */
    temperatureRange?: DisplayRange;
    humidityRange?: DisplayRange;
    windSpeedRange?: DisplayRange;
};

type WeatherChartProps = {
    data: RawWeatherRecord;
    /**
     * Per-hour min/max across the project (keyed like `data`). When present a
     * translucent band is drawn behind each line and the tooltip shows the
     * range; absent/null = single-point weather, chart unchanged.
     */
    spread?: WeatherSpread | null;
    /**
     * Which across-project range band to draw behind its centre line.  Only
     * one series is banded at a time so the chart stays readable; the
     * per-hour ranges for every series are always in the tooltip.
     */
    rangeSeries?: RangeSeries;
    onPointClick?: (timestamp: string, values: RawWeatherRecord[string], event: MouseEvent) => void;
    overriddenTimestamps?: Set<string>;
    selectedTimestamp?: string | null;
};

const TEMPERATURE_COLOR = "#ff6b35";
const HUMIDITY_COLOR = "#64b5f6";
const WIND_COLOR = "#80cbc4";
const BAND_OPACITY = 0.14;

/** Which series' across-project range band to draw (one at a time). */
export type RangeSeries = "none" | "temperature" | "humidity" | "wind";

const formatRange = (range: DisplayRange | undefined, digits: number, unit: string): string | null => {
    if (!range) return null;
    const [lo, hi] = range;
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
    const a = formatNumber(Math.min(lo, hi), { maximumFractionDigits: digits });
    const b = formatNumber(Math.max(lo, hi), { maximumFractionDigits: digits });
    return `${a}–${b}${unit} across project`;
};

const RangeCaption: React.FC<{ text: string | null }> = ({ text }) =>
    text ? (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", ml: 3.5, mt: -0.5, mb: 0.5 }}>
            {text}
        </Typography>
    ) : null;

interface CustomTickProps {
    x?: number;
    y?: number;
    payload?: {
        value: string;
    };
    windDirMap: Record<string, number>;
    windSpeedMap: Record<string, number>;
    speedUnit: string;
    tz: string;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: any;
    label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload}) => {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;

        const tz = dataPoint?.timezone as string | undefined;
        const displayTimestamp = tz
          ? dayjs(dataPoint.timestamp).tz(tz).format('dddd, MMM D, YYYY h:mm a ZZ')
          : dayjs(dataPoint.timestamp).format('dddd, MMM D, YYYY h:mm a');

        const temperatureUnit = (dataPoint?.temperatureUnit as string | undefined) ?? "°C";
        const speedUnit = (dataPoint?.speedUnit as string | undefined) ?? "km/h";

        return (
            <Box
                sx={{
                    backdropFilter: "blur(8px)",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                    boxShadow: 4,
                    padding: 2,
                    minWidth: 220,
                }}
            >
                <Typography variant="subtitle2" mb={1} color="textPrimary" fontWeight={600}>
                    {displayTimestamp}
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <DeviceThermostat fontSize="small" color="warning" />
                    <Typography variant="body2">
                        {formatNumber(dataPoint.temperature, { maximumFractionDigits: 1 })} {temperatureUnit}
                    </Typography>
                </Box>
                <RangeCaption text={formatRange(dataPoint.temperatureRange, 0, ` ${temperatureUnit}`)} />

                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <WaterDrop fontSize="small" color="primary" />
                    <Typography variant="body2">{dataPoint.humidity.toFixed(0)} % RH</Typography>
                </Box>
                <RangeCaption text={formatRange(dataPoint.humidityRange, 0, " % RH")} />

                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Air fontSize="small" color="info" />
                    <Typography variant="body2">
                        {formatNumber(dataPoint.windSpeed, { maximumFractionDigits: 1 })} {speedUnit}
                    </Typography>
                </Box>
                <RangeCaption text={formatRange(dataPoint.windSpeedRange, 0, ` ${speedUnit}`)} />

                <Box display="flex" alignItems="center" gap={1}>
                    <Navigation
                        fontSize="small"
                        style={{
                            transform: `rotate(${180 + dataPoint.windDir}deg)`,
                            transformOrigin: 'center',
                            color: '#1976d2',
                        }}
                    />
                    <Typography variant="body2">{dataPoint.windDir}°</Typography>
                </Box>
            </Box>
        );
    }
    return null;
};

const CustomTick: React.FC<CustomTickProps> = ({ x = 0, y = 0, payload, windDirMap, windSpeedMap, speedUnit, tz }) => {
    const rawTimestamp = payload?.value ?? "";
    const windDir = windDirMap[rawTimestamp] ?? 0;
    const windSpeed = windSpeedMap[rawTimestamp] ?? 0;

    // Format the display time inside the tick
    const dateObj = dayjs(rawTimestamp).tz(tz)
    const displayTime = dateObj.format("ha");

    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-35} y={0} width={70} height={70}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography
                        variant="caption"
                        style={{
                            fontSize: '0.7rem',
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}
                    >
                        {displayTime}
                    </Typography>
                    <Navigation
                        fontSize="small"
                        style={{
                            transform: `rotate(${180 + windDir}deg)`,
                            transformOrigin: "center",
                            margin: '2px 0',
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}
                    />
                    <Typography variant="caption" style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {formatNumber(windSpeed, { maximumFractionDigits: 0 })} {speedUnit}
                    </Typography>
                </Box>
            </foreignObject>
        </g>
    );
};

const WeatherChart: React.FC<WeatherChartProps> = ({ data, spread, rangeSeries = "none", onPointClick, overriddenTimestamps, selectedTimestamp }) => {
    const { selectedTimezone } = useTimezone();
    const unitSystem = useUnitSystem();
    const temperatureUnit = unitSystem === "imperial" ? "°F" : "°C";
    const speedUnit = unitSystem === "imperial" ? "mph" : "km/h";

    // 1. Guard against empty data
    if (!data || Object.keys(data).length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="350px">
                <Typography color="textSecondary">Loading weather data...</Typography>
                {/* Or use <CircularProgress /> from MUI */}
            </Box>
        );
    }

    // Spread arrives in metric like `data`; convert both ends with the same
    // display conversion the centre line uses so the band brackets the line.
    const toDisplayRange = (
        range: [number, number] | undefined,
        convert: (v: number) => number,
    ): DisplayRange | undefined => {
        if (!range || range.length !== 2) return undefined;
        const lo = convert(range[0]);
        const hi = convert(range[1]);
        if (!Number.isFinite(lo) || !Number.isFinite(hi)) return undefined;
        return [Math.min(lo, hi), Math.max(lo, hi)];
    };

    const processedData: ProcessedWeatherPoint[] = Object.entries(data).map(
        ([timestamp, values]) => {
            const hourSpread = spread?.[timestamp];
            return {
                timestamp, // Use this as the unique ID for Recharts
                time: dayjs(timestamp).tz(selectedTimezone).format("ha"), // Keep this for display
                temperature: toDisplayTemperature(values.temperature, unitSystem),
                windSpeed: toDisplaySpeed(values.wind_speed, unitSystem),
                humidity: values.relative_humidity,
                windDir: values.wind_direction,
                temperatureUnit,
                speedUnit,
                timezone: selectedTimezone,
                temperatureRange: toDisplayRange(hourSpread?.temperature, (v) => toDisplayTemperature(v, unitSystem)),
                humidityRange: toDisplayRange(hourSpread?.relative_humidity, (v) => v),
                windSpeedRange: toDisplayRange(hourSpread?.wind_speed, (v) => toDisplaySpeed(v, unitSystem)),
            };
        }
    );

    const hasSpread = processedData.some(
        (p) => p.temperatureRange || p.humidityRange || p.windSpeedRange
    );

    const windDirMap = processedData.reduce<Record<string, number>>((acc, { timestamp, windDir }) => {
        acc[timestamp] = windDir;
        return acc;
    }, {});

    const windSpeedMap = processedData.reduce<Record<string, number>>((acc, { timestamp, windSpeed }) => {
        acc[timestamp] = windSpeed;
        return acc;
    }, {});

    const renderCustomTick = (props: any) => (
        <CustomTick
            {...props}
            tz={selectedTimezone}
            windDirMap={windDirMap}
            windSpeedMap={windSpeedMap}
            speedUnit={speedUnit}
        />
    );

    const tickInterval = processedData.length <= 20 ? 0 : Math.ceil(processedData.length / 20);

    const handleChartClick = (chartData: any, event: MouseEvent) => {
        if (!onPointClick || !chartData?.activePayload?.length) return;
        const point = chartData.activePayload[0].payload as ProcessedWeatherPoint;
        const raw = data[point.timestamp];
        if (raw) onPointClick(point.timestamp, raw, event);
    };

    return (
        <Box style={{ width: "100%", height: "350px", position: "relative" }}>
            <ResponsiveContainer>
                <ComposedChart
                    data={processedData}
                    margin={{ top: 20, right: 25, left: 0, bottom: 60 }}
                    onClick={onPointClick ? handleChartClick : undefined}
                    style={onPointClick ? { cursor: 'pointer' } : undefined}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis
                        dataKey="timestamp"
                        tick={renderCustomTick}
                        interval={tickInterval}
                        tickFormatter={(value) => dayjs(value).tz(selectedTimezone).format("ha")}
                        stroke="rgba(255, 255, 255, 0.3)"
                    />
                    <YAxis
                        yAxisId="left"
                        label={{ value: `${temperatureUnit} / %`, angle: -90, position: "insideLeft", dx: 15, fill: 'rgba(255, 255, 255, 0.7)' }}
                        domain={[0, "auto"]}
                        stroke="rgba(255, 255, 255, 0.3)"
                        tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                    />
                    <YAxis
                        yAxisId="wind"
                        orientation="right"
                        label={{ value: speedUnit, angle: 90, position: "insideRight", dx: -10, fill: 'rgba(255, 255, 255, 0.5)' }}
                        domain={[0, "auto"]}
                        stroke="rgba(255, 255, 255, 0.2)"
                        tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 11 }}
                        width={40}
                    />
                    <Tooltip content={<CustomTooltip/>} />

                    {/* Min/max across the project for ONE series, drawn first so the centre lines sit on top. */}
                    {hasSpread && rangeSeries === "temperature" && (
                            <Area
                                type="monotone"
                                dataKey="temperatureRange"
                                yAxisId="left"
                                fill={TEMPERATURE_COLOR}
                                fillOpacity={BAND_OPACITY}
                                stroke="none"
                                activeDot={false}
                                isAnimationActive={false}
                                connectNulls
                                legendType="none"
                                tooltipType="none"
                                name="Temperature range across project"
                            />
                    )}
                    {hasSpread && rangeSeries === "humidity" && (
                            <Area
                                type="monotone"
                                dataKey="humidityRange"
                                yAxisId="left"
                                fill={HUMIDITY_COLOR}
                                fillOpacity={BAND_OPACITY}
                                stroke="none"
                                activeDot={false}
                                isAnimationActive={false}
                                connectNulls
                                legendType="none"
                                tooltipType="none"
                                name="Humidity range across project"
                            />
                    )}
                    {hasSpread && rangeSeries === "wind" && (
                            <Area
                                type="monotone"
                                dataKey="windSpeedRange"
                                yAxisId="wind"
                                fill={WIND_COLOR}
                                fillOpacity={BAND_OPACITY}
                                stroke="none"
                                activeDot={false}
                                isAnimationActive={false}
                                connectNulls
                                legendType="none"
                                tooltipType="none"
                                name="Wind speed range across project"
                            />
                    )}

                    <Line
                        type="monotone"
                        dataKey="temperature"
                        stroke={TEMPERATURE_COLOR}
                        yAxisId="left"
                        name={`Temperature (${temperatureUnit})`}
                        strokeWidth={3}
                        dot={{ r: 0.5 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="humidity"
                        stroke={HUMIDITY_COLOR}
                        yAxisId="left"
                        name="Humidity (%)"
                        strokeWidth={3}
                        dot={{ r: 0.5 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="windSpeed"
                        stroke={WIND_COLOR}
                        yAxisId="wind"
                        name={`Wind speed (${speedUnit})`}
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        dot={false}
                    />
                    {overriddenTimestamps && processedData
                        .filter((p) => overriddenTimestamps.has(p.timestamp))
                        .map((p) => (
                            <ReferenceDot
                                key={p.timestamp}
                                x={p.timestamp}
                                y={p.temperature}
                                yAxisId="left"
                                r={5}
                                fill="#ff6b35"
                                stroke="#fff"
                                strokeWidth={1.5}
                                ifOverflow="extendDomain"
                            />
                        ))
                    }
                    {selectedTimestamp && (() => {
                        const sel = processedData.find((p) => p.timestamp === selectedTimestamp);
                        return sel ? (
                            <ReferenceDot
                                x={sel.timestamp}
                                y={sel.temperature}
                                yAxisId="left"
                                r={7}
                                fill="transparent"
                                stroke="#ff6b35"
                                strokeWidth={2}
                                ifOverflow="extendDomain"
                            />
                        ) : null;
                    })()}
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default WeatherChart;
